import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, Camera, Filter, ArrowRight, Upload, Check, X } from 'lucide-react';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { useSEO } from '../hooks/useSEO';
import { Link } from 'react-router-dom';
// Fix Leaflet default marker icons
const customIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
  className: 'hue-rotate-[140deg] saturate-[0.5]' // Adjust color slightly via CSS filter if needed, or just use default blue for now
});
// Images
const HERO_IMAGE = 'https://cdn.magicpatterns.com/uploads/bwQjjTaBFPGmsVMM5LvtNo/_DSC8285.webp';
const FEATURED_1 = 'https://cdn.magicpatterns.com/uploads/h58wUekQZycBGPqmB5effo/682effc0ba43af376950f23b_renders_robeta.jpg';
const FEATURED_2 = 'https://cdn.magicpatterns.com/uploads/pKPzBNwMUWtZUzQjDCs91d/6842e88f2f523e985c5fa1a4__DSC8349-2.jpg';
const FEATURED_3 = 'https://cdn.magicpatterns.com/uploads/iSsLTMw4wKgenTWkbWEAxu/_DSC8170.webp';
// Mock Data
const MOMENTS = [{
  id: 1,
  title: 'Sunrise in Banff',
  author: 'Sarah & Mike',
  location: 'Banff National Park, AB',
  model: 'Mythos 4 Two',
  type: 'National Park',
  lat: 51.1784,
  lng: -115.5708,
  image: FEATURED_1,
  desc: 'Woke up to the most incredible light hitting the peaks. The heating system kept us cozy all night despite the -10C drop.',
  slug: 'sunrise-in-banff'
}, {
  id: 2,
  title: 'Coastal Drive',
  author: 'James D.',
  location: 'Cabot Trail, NS',
  model: 'Helios',
  type: 'Coastal',
  lat: 46.816,
  lng: -60.648,
  image: FEATURED_2,
  desc: 'The winding roads were a dream. The van handles like a car, making these tight turns effortless.',
  slug: 'coastal-drive-cabot-trail'
}, {
  id: 3,
  title: 'Desert Solitude',
  author: 'Elena R.',
  location: 'Joshua Tree, CA',
  model: 'Apollo',
  type: 'Off-grid',
  lat: 33.8734,
  lng: -115.901,
  image: FEATURED_3,
  desc: 'Three days off-grid with zero issues. The solar setup is a game changer for remote work.',
  slug: 'desert-solitude-joshua-tree'
}];
const FILTERS = ['All', 'Off-grid', 'National Park', 'Coastal', 'Mountains', 'Winter'];
export function MomentsOnTheMapPage() {
  useSEO({
    title: 'Moments on the Map — Community Journeys',
    description: 'Discover where Robeta takes you. A collection of journeys shared by our community of explorers across North America.'
  });
  const [activeFilter, setActiveFilter] = useState('All');
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const filteredMoments = activeFilter === 'All' ? MOMENTS : MOMENTS.filter(m => m.type === activeFilter);
  return <main className="bg-white min-h-screen pt-32 md:pt-40" data-id="element-575">
      {/* 1. COMPACT PAGE HEADER */}
      <section className="container mx-auto px-6 md:px-12 mb-12" data-id="element-576">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} className="flex flex-col md:flex-row md:items-end md:justify-between gap-8" data-id="element-577">
          <div className="max-w-2xl" data-id="element-578">
            <span className="text-robeta-copper text-xs font-bold uppercase tracking-wide-editorial block mb-6" data-id="element-579">
              Community
            </span>
            <h1 className="text-4xl md:text-6xl font-bold text-robeta-black uppercase tracking-editorial leading-tight mb-4" data-id="element-580">
              Moments on the Map
            </h1>
            <p className="text-lg md:text-xl font-light text-robeta-black/60 leading-relaxed" data-id="element-581">
              Discover where Robeta takes you. A collection of journeys shared
              by our community of explorers.
            </p>
          </div>
          <button onClick={() => {
          const element = document.getElementById('share-section');
          element?.scrollIntoView({
            behavior: 'smooth'
          });
        }} className="px-10 py-4 bg-robeta-black text-white font-bold uppercase tracking-widest hover:bg-robeta-copper transition-colors flex-shrink-0 self-start md:self-auto" data-id="element-582">
            Share Your Journey
          </button>
        </motion.div>
      </section>

      {/* 2. INTERACTIVE MAP SECTION */}
      <section className="pb-24 bg-white" data-id="element-583">
        <div className="container mx-auto px-6 md:px-12 mb-6" data-id="element-584">
          {/* Filters */}
          <div className="flex flex-wrap gap-3" data-id="element-585">
            {FILTERS.map(filter => <button key={filter} onClick={() => setActiveFilter(filter)} className={`px-4 py-2 text-xs font-bold uppercase tracking-widest border transition-colors ${activeFilter === filter ? 'bg-robeta-black text-white border-robeta-black' : 'bg-white text-robeta-black border-robeta-black/10 hover:border-robeta-copper'}`} data-id="element-586">
                {filter}
              </button>)}
          </div>
        </div>

        <div className="w-full h-[70vh] bg-gray-100 relative" data-id="element-587">
          <MapContainer center={[45, -100]} zoom={4} scrollWheelZoom={false} className="w-full h-full z-0" style={{
          background: '#f4f4f2'
        }} data-id="element-588">
            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/">CARTO</a>' url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" data-id="element-589" />
            {filteredMoments.map(moment => <Marker key={moment.id} position={[moment.lat, moment.lng]} icon={customIcon} data-id="element-590">
                <Popup className="custom-popup" data-id="element-591">
                  <div className="w-64 font-sans" data-id="element-592">
                    <div className="aspect-video w-full overflow-hidden mb-3 rounded-sm" data-id="element-593">
                      <img src={moment.image} alt={moment.title} className="w-full h-full object-cover" data-id="element-594" />
                    </div>
                    <span className="text-robeta-copper text-[10px] font-bold uppercase tracking-widest block mb-1" data-id="element-595">
                      {moment.type}
                    </span>
                    <h3 className="text-robeta-black font-bold text-sm uppercase tracking-wide mb-1" data-id="element-596">
                      {moment.title}
                    </h3>
                    <p className="text-robeta-black/60 text-xs mb-2 italic" data-id="element-597">
                      by {moment.author}
                    </p>
                    <p className="text-robeta-black/80 text-xs line-clamp-2 mb-3" data-id="element-598">
                      {moment.desc}
                    </p>
                    <div className="flex items-center justify-between border-t border-gray-100 pt-2" data-id="element-599">
                      <span className="text-[10px] text-robeta-black/40 uppercase tracking-wider" data-id="element-600">
                        {moment.model}
                      </span>
                      <span className="text-[10px] font-bold text-robeta-black uppercase tracking-wider cursor-pointer hover:text-robeta-copper" data-id="element-601">
                        View Full
                      </span>
                    </div>
                  </div>
                </Popup>
              </Marker>)}
          </MapContainer>
        </div>
      </section>

      {/* 3. FEATURED JOURNEYS */}
      <section className="py-24 md:py-32 bg-robeta-mist/10" data-id="element-602">
        <div className="container mx-auto px-6 md:px-12" data-id="element-603">
          <div className="text-center mb-20" data-id="element-604">
            <h2 className="text-3xl md:text-5xl font-bold text-robeta-black uppercase tracking-editorial mb-6" data-id="element-605">
              Featured Journeys
            </h2>
            <div className="w-24 h-px bg-robeta-copper mx-auto" data-id="element-606" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-607">
            {MOMENTS.map((moment, index) => <motion.div key={moment.id} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: index * 0.1
          }} className="group cursor-pointer" data-id="element-608">
                <div className="aspect-[4/5] overflow-hidden mb-6 relative" data-id="element-609">
                  <img src={moment.image} alt={moment.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-610" />
                  <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1" data-id="element-611">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-robeta-black" data-id="element-612">
                      {moment.location}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between mb-3" data-id="element-613">
                  <span className="text-robeta-copper text-xs font-bold uppercase tracking-wide-editorial" data-id="element-614">
                    {moment.type}
                  </span>
                  <span className="text-robeta-black/40 text-xs uppercase tracking-wider" data-id="element-615">
                    {moment.model}
                  </span>
                </div>
                <h3 className="text-2xl font-bold text-robeta-black uppercase tracking-editorial mb-3 group-hover:text-robeta-copper transition-colors" data-id="element-616">
                  {moment.title}
                </h3>
                <p className="text-robeta-black/60 font-light leading-relaxed mb-6 line-clamp-3" data-id="element-617">
                  {moment.desc}
                </p>
                <Link to={`/blog/${moment.slug}`} className="inline-flex items-center text-xs font-bold uppercase tracking-widest text-robeta-black border-b border-robeta-black/20 pb-1 group-hover:border-robeta-copper transition-colors" data-id="element-618">
                  Read Story <ArrowRight className="ml-2 w-3 h-3" data-id="element-619" />
                </Link>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* 4. SHARE YOUR JOURNEY */}
      <section id="share-section" className="py-24 md:py-32 bg-white" data-id="element-620">
        <div className="container mx-auto px-6 md:px-12" data-id="element-621">
          <div className="flex flex-col lg:flex-row gap-16 lg:gap-24" data-id="element-622">
            <div className="w-full lg:w-1/3" data-id="element-623">
              <span className="text-robeta-copper text-xs font-bold uppercase tracking-wide-editorial block mb-6" data-id="element-624">
                Contribute
              </span>
              <h2 className="text-3xl md:text-5xl font-bold text-robeta-black uppercase tracking-editorial mb-8" data-id="element-625">
                Share Your
                <br data-id="element-626" />
                Journey
              </h2>
              <p className="text-robeta-black/60 font-light leading-relaxed mb-8" data-id="element-627">
                Join our community of explorers. Upload a photo from your Robeta
                adventure, and we'll add it to the map using the embedded
                location data.
              </p>
              <ul className="space-y-4" data-id="element-628">
                {['Automatic GPS extraction', 'Community featured stories', 'Exclusive owner badges'].map((item, i) => <li key={i} className="flex items-center text-sm text-robeta-black/70" data-id="element-629">
                    <Check className="w-4 h-4 text-robeta-copper mr-3" data-id="element-630" />
                    {item}
                  </li>)}
              </ul>
            </div>

            <div className="w-full lg:w-2/3 bg-gray-50 p-8 md:p-12 border border-robeta-black/5" data-id="element-631">
              <form className="grid grid-cols-1 md:grid-cols-2 gap-6" onSubmit={e => e.preventDefault()} data-id="element-632">
                <div className="col-span-1" data-id="element-633">
                  <label className="block text-xs font-bold uppercase tracking-widest text-robeta-black mb-2" data-id="element-634">
                    Name
                  </label>
                  <input type="text" placeholder="Your Name" className="w-full px-4 py-3 border border-robeta-black/10 bg-white focus:border-robeta-copper focus:outline-none transition-colors text-sm" data-id="element-635" />
                </div>
                <div className="col-span-1" data-id="element-636">
                  <label className="block text-xs font-bold uppercase tracking-widest text-robeta-black mb-2" data-id="element-637">
                    Model
                  </label>
                  <select className="w-full px-4 py-3 border border-robeta-black/10 bg-white focus:border-robeta-copper focus:outline-none transition-colors text-sm appearance-none" data-id="element-638">
                    <option data-id="element-639">Select Model...</option>
                    <option data-id="element-640">Mythos 4 Two</option>
                    <option data-id="element-641">Apollo</option>
                    <option data-id="element-642">Helios</option>
                    <option data-id="element-643">Kronos</option>
                  </select>
                </div>
                <div className="col-span-1" data-id="element-644">
                  <label className="block text-xs font-bold uppercase tracking-widest text-robeta-black mb-2" data-id="element-645">
                    Location
                  </label>
                  <div className="relative" data-id="element-646">
                    <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-robeta-black/30" data-id="element-647" />
                    <input type="text" placeholder="City, Region (or use photo GPS)" className="w-full pl-10 pr-4 py-3 border border-robeta-black/10 bg-white focus:border-robeta-copper focus:outline-none transition-colors text-sm" data-id="element-648" />
                  </div>
                </div>
                <div className="col-span-1" data-id="element-649">
                  <label className="block text-xs font-bold uppercase tracking-widest text-robeta-black mb-2" data-id="element-650">
                    Trip Type
                  </label>
                  <select className="w-full px-4 py-3 border border-robeta-black/10 bg-white focus:border-robeta-copper focus:outline-none transition-colors text-sm appearance-none" data-id="element-651">
                    <option data-id="element-652">Select Type...</option>
                    <option data-id="element-653">Off-grid</option>
                    <option data-id="element-654">National Park</option>
                    <option data-id="element-655">Coastal</option>
                    <option data-id="element-656">Mountains</option>
                    <option data-id="element-657">Winter</option>
                  </select>
                </div>
                <div className="col-span-1 md:col-span-2" data-id="element-658">
                  <label className="block text-xs font-bold uppercase tracking-widest text-robeta-black mb-2" data-id="element-659">
                    Story
                  </label>
                  <textarea rows={4} placeholder="Tell us about this moment..." className="w-full px-4 py-3 border border-robeta-black/10 bg-white focus:border-robeta-copper focus:outline-none transition-colors text-sm resize-none" data-id="element-660" />
                </div>

                {/* File Upload Area */}
                <div className="col-span-1 md:col-span-2" data-id="element-661">
                  <div className="border-2 border-dashed border-robeta-black/10 hover:border-robeta-copper/50 transition-colors p-8 text-center cursor-pointer bg-white" data-id="element-662">
                    <Camera className="w-8 h-8 text-robeta-black/20 mx-auto mb-4" data-id="element-663" />
                    <p className="text-sm text-robeta-black/60 font-medium mb-1" data-id="element-664">
                      Drag and drop your photo here
                    </p>
                    <p className="text-xs text-robeta-black/40" data-id="element-665">
                      JPG or PNG, max 10MB
                    </p>
                  </div>
                </div>

                <div className="col-span-1 md:col-span-2 flex items-start gap-3 mt-4" data-id="element-666">
                  <input type="checkbox" className="mt-1" data-id="element-667" />
                  <p className="text-xs text-robeta-black/60 leading-relaxed" data-id="element-668">
                    I consent to Robeta Canada using this photo and story for
                    marketing purposes. I confirm I own the rights to this
                    content.
                  </p>
                </div>

                <div className="col-span-1 md:col-span-2 mt-4" data-id="element-669">
                  <button className="w-full py-4 bg-robeta-black text-white font-bold uppercase tracking-widest hover:bg-robeta-copper transition-colors" data-id="element-670">
                    Submit Your Journey
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* 5. COMMUNITY STATEMENT */}
      <section className="py-32 md:py-48 bg-robeta-black text-white text-center" data-id="element-671">
        <div className="container mx-auto px-6 md:px-12" data-id="element-672">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-673">
            <span className="text-robeta-copper text-xs font-bold uppercase tracking-wide-editorial block mb-8" data-id="element-674">
              Our Community
            </span>
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-light uppercase tracking-editorial leading-tight mb-8" data-id="element-675">
              Adventure. Freedom.
              <br data-id="element-676" />
              <span className="text-white/50" data-id="element-677">Shared together.</span>
            </h2>
            <p className="text-white/40 font-light text-lg max-w-2xl mx-auto leading-relaxed" data-id="element-678">
              From the rugged coasts of Newfoundland to the peaks of the
              Rockies, the Robeta family is exploring every corner of this
              continent.
            </p>
          </motion.div>
        </div>
      </section>
    </main>;
}